import { useEffect, useState, useRef, useCallback } from "react";
import { Bell, Heart, MessageCircle, Eye, BadgeCheck, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";

const TYPE_META = {
  interest_received:  { icon: Heart,        copy: (n) => `${n.from_user?.name || "Someone"} sent you an interest`, target: () => "/dashboard" },
  interest_accepted:  { icon: BadgeCheck,   copy: (n) => `${n.from_user?.name || "Someone"} accepted your interest`, target: (n) => `/profile/${n.from_user_id}` },
  interest_rejected:  { icon: X,            copy: (n) => `${n.from_user?.name || "Someone"} declined your interest`, target: () => "/dashboard" },
  new_message:        { icon: MessageCircle, copy: (n) => `${n.from_user?.name || "Someone"} sent you a message`, target: (n) => `/messages?to=${n.from_user_id}` },
  profile_view:       { icon: Eye,          copy: (n) => `${n.from_user?.name || "Someone"} viewed your profile`, target: (n) => `/profile/${n.from_user_id}` },
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  const fetchItems = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.results || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    fetchItems();
    const t = setInterval(fetchItems, 30000);
    return () => clearInterval(t);
  }, [fetchItems]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const handleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
      // Optimistically mark local items as read
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
    }
  };

  const onItem = (n) => {
    setOpen(false);
    const meta = TYPE_META[n.type];
    if (meta) navigate(meta.target(n));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-full hover:bg-brand-muted transition-colors text-brand-ink"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" strokeWidth={1.5} />
        {unread > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-brand-maroon text-white text-[10px] font-semibold flex items-center justify-center" data-testid="notification-bell-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto bg-white border border-brand-line rounded-lg shadow-2xl z-50" data-testid="notification-dropdown">
          <div className="px-4 py-3 border-b border-brand-line flex items-center justify-between sticky top-0 bg-white">
            <p className="overline">Notifications</p>
            {items.length > 0 && <span className="text-[10px] text-brand-inkMute tracking-wide uppercase">{items.length}</span>}
          </div>
          {items.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Bell className="h-8 w-8 text-brand-inkMute mx-auto" strokeWidth={1.2} />
              <p className="text-sm text-brand-inkLight mt-3">No notifications yet</p>
              <p className="text-xs text-brand-inkMute mt-1">We&apos;ll alert you when someone shows interest.</p>
            </div>
          ) : (
            <ul className="divide-y divide-brand-line">
              {items.map((n) => {
                const meta = TYPE_META[n.type] || { icon: Bell, copy: () => n.type, target: () => "/dashboard" };
                const Icon = meta.icon;
                return (
                  <li key={n.id}>
                    <button onClick={() => onItem(n)} className="w-full text-left px-4 py-3 hover:bg-brand-muted/60 transition-colors flex items-start gap-3" data-testid={`notification-item-${n.id}`}>
                      {n.from_user?.photo_url ? (
                        <img src={fileUrl(n.from_user.photo_url)} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <span className="w-8 h-8 rounded-full bg-brand-muted text-brand-maroon flex items-center justify-center flex-shrink-0">
                          <Icon className="h-4 w-4" strokeWidth={1.5} />
                        </span>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-brand-ink leading-snug">{meta.copy(n)}</p>
                        <p className="text-[10px] text-brand-inkMute mt-1 tracking-wide">{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-brand-gold flex-shrink-0 mt-2" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {items.length > 0 && (
            <button
              onClick={() => { setOpen(false); navigate("/notifications"); }}
              className="w-full px-4 py-3 text-xs text-brand-maroon hover:bg-brand-muted/60 transition-colors border-t border-brand-line text-center tracking-[0.15em] uppercase font-medium"
              data-testid="notification-view-all"
            >
              View all →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
